import { useEffect, useState } from "react";
import { osApi, formatINR, statusColor, stageColor, timeAgo } from "./osApi";
import { toast } from "sonner";
import { X, Phone, MessageCircle, MapPin, Save, PhoneCall, CalendarDays } from "lucide-react";

export default function OSLeadDrawer({ leadId, onClose, onUpdate }) {
  const [lead, setLead] = useState(null);
  const [meta, setMeta] = useState(null);
  const [team, setTeam] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [l, m] = await Promise.all([osApi.getLead(leadId), osApi.meta()]);
        if (cancelled) return;
        setLead(l); setMeta(m);
        setEdit({ stage: l.stage, lead_status: l.lead_status, assigned_agent: l.assigned_agent, next_followup: l.next_followup?.slice(0,16) || "" });
        // Team list is manager+admin only; agents/telecallers won't have it
        try {
          const t = await osApi.listTeam();
          if (!cancelled) setTeam(t.items || []);
        } catch { /* not allowed — hide reassign UI */ }
      } catch (e) { toast.error("Failed to load lead"); }
    })();
    return () => { cancelled = true; };
  }, [leadId]);

  if (!lead) {
    return (
      <div className="os-drawer-overlay" onClick={onClose}>
        <div className="os-drawer" onClick={e => e.stopPropagation()}>
          <div className="p-8 text-center" style={{color:"var(--os-text-muted)"}}>Loading lead…</div>
        </div>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const upd = { ...edit };
      if (upd.next_followup && upd.next_followup.length >= 10) {
        upd.next_followup = new Date(upd.next_followup).toISOString();
      } else { delete upd.next_followup; }
      const res = await osApi.updateLead(lead.id, upd);
      setLead(res); onUpdate?.();
      toast.success("Lead updated");
    } catch (e) { toast.error("Update failed"); } finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try { const res = await osApi.addNote(lead.id, note); setLead(res); setNote(""); onUpdate?.(); toast.success("Note added"); }
    catch (e) { toast.error("Failed"); }
  };

  const markContacted = async () => {
    try { const res = await osApi.contacted(lead.id); setLead(res); onUpdate?.(); toast.success("Marked contacted"); }
    catch (e) { toast.error("Failed"); }
  };

  const assign = async (agentId) => {
    try {
      const res = await osApi.assign(lead.id, agentId);
      setLead(res); onUpdate?.(); toast.success("Agent assigned");
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };

  return (
    <>
      <div className="os-drawer-overlay" onClick={onClose} />
      <aside className="os-drawer" data-testid="os-lead-drawer">
        <div className="p-5 border-b sticky top-0 z-10" style={{background:"var(--os-bg)", borderColor:"var(--os-border)"}}>
          <div className="flex items-start justify-between">
            <div>
              <div className="os-mono text-xs mb-1" style={{color:"var(--os-text-muted)"}}>{lead.lead_id}</div>
              <div className="text-xl font-bold">{lead.customer_name}</div>
              <div className="text-xs mt-1" style={{color:"var(--os-text-muted)"}}>Created {timeAgo(lead.created_at)}</div>
            </div>
            <button onClick={onClose} data-testid="os-drawer-close" className="p-1.5 rounded-md hover:bg-white/5"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex gap-2 mt-4 flex-wrap">
            <a href={`tel:${lead.phone}`} className="os-btn" data-testid="os-action-call"><Phone className="w-3.5 h-3.5" /> Call</a>
            <a href={`https://wa.me/91${lead.phone}?text=${encodeURIComponent(`Hi ${lead.customer_name}, this is Our First Realty regarding your inquiry.`)}`} target="_blank" rel="noreferrer" className="os-btn-ghost" data-testid="os-action-whatsapp"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
            <button className="os-btn-ghost" onClick={markContacted} data-testid="os-action-contacted"><PhoneCall className="w-3.5 h-3.5" /> Contacted</button>
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* Status row */}
          <div className="flex flex-wrap gap-2">
            <span className={`os-badge ${stageColor(lead.stage)}`}>{lead.stage}</span>
            <span className={`os-badge ${statusColor(lead.lead_status)}`}>{lead.lead_status}</span>
            <span className="os-badge os-badge-blue">{lead.source}</span>
            {lead.responded_at ? <span className="os-badge os-badge-green">Responded</span> : <span className={`os-badge ${lead.sla_status === "Breached" ? "os-badge-red" : "os-badge-amber"}`}>{lead.sla_status}</span>}
          </div>

          {/* Contact + Property */}
          <Section title="Contact">
            <Field label="Phone" value={lead.phone} mono />
            <Field label="Email" value={lead.email || "—"} />
            <Field label="Source" value={lead.source} />
            {lead.connector_name && <Field label="Connector" value={lead.connector_name} />}
          </Section>

          <Section title="Property Requirement">
            <Field label="Type" value={lead.property_type || "—"} />
            <Field label="Budget" value={formatINR(lead.budget)} mono />
            <Field label="Location" value={lead.location || "—"} icon={MapPin} />
            <Field label="Purpose" value={lead.purpose || "—"} />
            <Field label="Timeline" value={lead.timeline || "—"} />
          </Section>

          {lead.loan_required && (
            <Section title="Loan Eligibility">
              <Field label="Type" value={lead.loan_type || "—"} />
              <Field label="Loan Amount" value={formatINR(lead.loan_amount)} mono />
              <Field label="Monthly Income" value={formatINR(lead.income)} mono />
              <Field label="CIBIL" value={lead.cibil ?? "—"} mono />
              <Field label="Eligibility" value={lead.eligibility_score != null ? `${lead.eligibility_score}%` : "—"} mono />
            </Section>
          )}

          {/* Edit block */}
          <Section title="Update">
            <label className="block">
              <div className="text-xs mb-1" style={{color:"var(--os-text-muted)"}}>Stage</div>
              <select className="os-input" value={edit.stage} onChange={e => setEdit({...edit, stage: e.target.value})} data-testid="os-edit-stage">
                {meta?.stages?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs mb-1" style={{color:"var(--os-text-muted)"}}>AI Status</div>
              <select className="os-input" value={edit.lead_status} onChange={e => setEdit({...edit, lead_status: e.target.value})} data-testid="os-edit-status">
                {meta?.statuses?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs mb-1" style={{color:"var(--os-text-muted)"}}>Assigned Agent</div>
              <select className="os-input" value={edit.assigned_agent || ""} onChange={e => { setEdit({...edit, assigned_agent: e.target.value}); assign(e.target.value); }} data-testid="os-edit-agent">
                <option value="">Unassigned</option>
                {team.filter(t => t.role !== "admin").map(t => <option key={t.id} value={t.id}>{t.name} · {t.role}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs mb-1" style={{color:"var(--os-text-muted)"}}>Next follow-up</div>
              <input type="datetime-local" className="os-input" value={edit.next_followup || ""} onChange={e => setEdit({...edit, next_followup: e.target.value})} data-testid="os-edit-followup" />
            </label>
            <button onClick={save} disabled={saving} className="os-btn" data-testid="os-save-lead"><Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}</button>
          </Section>

          <Section title="Notes / Objections">
            {lead.objection && <div className="text-xs mb-2 os-badge os-badge-amber">{lead.objection}</div>}
            <textarea className="os-input" rows={3} placeholder="Add a note or follow-up log…" value={note} onChange={e => setNote(e.target.value)} data-testid="os-note-input" />
            <button onClick={addNote} disabled={!note.trim()} className="os-btn-ghost mt-2" data-testid="os-note-add">Add note</button>
            {lead.notes && (
              <pre className="mt-3 text-xs whitespace-pre-wrap os-mono p-3 rounded-md" style={{background:"var(--os-card)", color:"var(--os-text-muted)", border:"1px solid var(--os-border)"}}>
                {lead.notes}
              </pre>
            )}
          </Section>
        </div>
      </aside>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div className="os-card p-4 space-y-3">
      <div className="text-[11px] uppercase tracking-widest" style={{color: "var(--os-text-muted)"}}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, mono, icon: Icon }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5" style={{borderBottom: "1px dashed var(--os-border)"}}>
      <div className="text-xs" style={{color:"var(--os-text-muted)"}}>{label}</div>
      <div className={`text-sm text-right ${mono ? "os-mono" : ""} flex items-center gap-1.5`}>
        {Icon && <Icon className="w-3 h-3" style={{color:"var(--os-text-dim)"}} />} {value}
      </div>
    </div>
  );
}
